import { Box, Button, Checkbox, FormControlLabel, Typography, TextField, FormLabel, IconButton } from "@mui/material";
import { Settings, ExpandLess } from "@mui/icons-material";
import { useState } from "react";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateTime } from "luxon";
import { goalsApi, CreateGoalData } from "../../../../../utils/goalsApi";
import { errorToastMessage, toastMessage } from "../../../../../utils/toast";

interface GoalConfig {
    title: string;
    description: string;
    reminderTimes: DateTime[];
}

interface ExtractedGoalsPanelProps {
    goals: string[];
    participantId: string;
    noteId?: string;
    onDismiss: () => void;
    onGoalsCreated: () => void;
}

const ExtractedGoalsPanel = ({
    goals,
    participantId,
    noteId,
    onDismiss,
    onGoalsCreated,
}: ExtractedGoalsPanelProps) => {
    const [selectedGoals, setSelectedGoals] = useState<Set<string>>(new Set());
    const [goalConfigs, setGoalConfigs] = useState<Map<string, GoalConfig>>(new Map());
    const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);

    const handleToggleGoal = (goal: string) => {
        const newSelected = new Set(selectedGoals);
        if (newSelected.has(goal)) {
            newSelected.delete(goal);
            // Remove config and expanded state when unchecked
            const newConfigs = new Map(goalConfigs);
            newConfigs.delete(goal);
            setGoalConfigs(newConfigs);
            const newExpanded = new Set(expandedGoals);
            newExpanded.delete(goal);
            setExpandedGoals(newExpanded);
        } else {
            newSelected.add(goal);
            // Initialize config when checked
            const newConfigs = new Map(goalConfigs);
            newConfigs.set(goal, {
                title: goal,
                description: "",
                reminderTimes: [],
            });
            setGoalConfigs(newConfigs);
            // Auto-expand when first selected
            const newExpanded = new Set(expandedGoals);
            newExpanded.add(goal);
            setExpandedGoals(newExpanded);
        }
        setSelectedGoals(newSelected);
    };

    const handleToggleExpand = (goal: string) => {
        const newExpanded = new Set(expandedGoals);
        if (newExpanded.has(goal)) {
            newExpanded.delete(goal);
        } else {
            newExpanded.add(goal);
        }
        setExpandedGoals(newExpanded);
    };

    const updateGoalConfig = (goal: string, field: keyof GoalConfig, value: any) => {
        const newConfigs = new Map(goalConfigs);
        const config = newConfigs.get(goal);
        if (config) {
            newConfigs.set(goal, { ...config, [field]: value });
            setGoalConfigs(newConfigs);
        }
    };

    const addReminderTime = (goal: string) => {
        const config = goalConfigs.get(goal);
        if (config) {
            // Set time to current hour with minutes = 0
            const now = DateTime.now();
            const timeWithZeroMinutes = now.set({ minute: 0, second: 0, millisecond: 0 });
            updateGoalConfig(goal, "reminderTimes", [...config.reminderTimes, timeWithZeroMinutes]);
        }
    };

    const removeReminderTime = (goal: string, index: number) => {
        const config = goalConfigs.get(goal);
        if (config) {
            updateGoalConfig(goal, "reminderTimes", config.reminderTimes.filter((_, i) => i !== index));
        }
    };

    const updateReminderTime = (goal: string, index: number, time: DateTime | null) => {
        if (time) {
            const config = goalConfigs.get(goal);
            if (config) {
                const newTimes = [...config.reminderTimes];
                // Always set minutes to 0
                newTimes[index] = time.set({ minute: 0, second: 0, millisecond: 0 });
                updateGoalConfig(goal, "reminderTimes", newTimes);
            }
        }
    };

    const handleBulkAssign = async () => {
        try {
            setSaving(true);
            const goalsToCreate: CreateGoalData[] = Array.from(selectedGoals).map((goal) => {
                const config = goalConfigs.get(goal)!;
                return {
                    title: config.title,
                    description: config.description || undefined,
                    reminderTimes: config.reminderTimes.map((time) => time.toFormat("HH:mm")),
                    participantId,
                    noteId: noteId,
                };
            });

            // Create all goals in bulk
            await goalsApi.createBulkGoals(goalsToCreate);

            toastMessage("success", `${goalsToCreate.length} goal(s) assigned successfully`);
            onGoalsCreated();
        } catch (error) {
            errorToastMessage(error as Error);
        } finally {
            setSaving(false);
        }
    };

    if (goals.length === 0) {
        return null;
    }

    return (
        <Box>
            <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                Select and configure goals to assign
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                {goals.map((goal, index) => {
                    const isSelected = selectedGoals.has(goal);
                    const config = goalConfigs.get(goal);

                    const isExpanded = expandedGoals.has(goal);

                    return (
                        <Box
                            key={index}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "4px",
                                p: 1.5,
                                // "&:hover": {
                                //     bgcolor: "#f5f5f5",
                                // },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleToggleGoal(goal)}
                                        />
                                    }
                                    label={goal}
                                    sx={{ flex: 1 }}
                                />
                                {isSelected && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleToggleExpand(goal)}
                                        sx={{ ml: 1 }}
                                    >
                                        {isExpanded ? <ExpandLess /> : <Settings />}
                                    </IconButton>
                                )}
                            </Box>

                            {isSelected && config && isExpanded && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        pl: 4,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1.5,
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                        pt: 2,
                                    }}
                                >
                                    {/* First row: Title */}
                                    <TextField
                                        size="small"
                                        label="Title"
                                        value={config.title}
                                        onChange={(e) => updateGoalConfig(goal, "title", e.target.value)}
                                        fullWidth
                                    />

                                    {/* Second row: Description */}
                                    <TextField
                                        size="small"
                                        label="Description (Optional)"
                                        value={config.description}
                                        onChange={(e) => updateGoalConfig(goal, "description", e.target.value)}
                                        multiline
                                        minRows={2}
                                        fullWidth
                                    />

                                    {/* Third row: Reminder Times */}
                                    <Box>
                                        <FormLabel sx={{ fontSize: "0.75rem", mb: 1, display: "block" }}>
                                            Reminder Times (Optional)
                                        </FormLabel>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                            {config.reminderTimes.map((time, timeIndex) => (
                                                <Box key={timeIndex} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                    <TimePicker
                                                        label="Time"
                                                        value={time}
                                                        onChange={(newValue: DateTime | null) => updateReminderTime(goal, timeIndex, newValue)}
                                                        views={['hours']}
                                                        renderInput={(params: any) => (
                                                            <TextField {...params} size="small" sx={{ width: 150 }} />
                                                        )}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => removeReminderTime(goal, timeIndex)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Box>
                                            ))}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => addReminderTime(goal)}
                                                sx={{ width: 180 }}
                                            >
                                                Add Reminder Time
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={onDismiss} disabled={saving}>
                    Dismiss
                </Button>
                <Button
                    variant="contained"
                    onClick={handleBulkAssign}
                    disabled={selectedGoals.size === 0 || saving}
                >
                    {saving ? "Assigning..." : `Assign ${selectedGoals.size} Goal(s)`}
                </Button>
            </Box>
        </Box>
    );
};

export default ExtractedGoalsPanel;
